import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class AccessibilityObserverService {
    private observer!: MutationObserver | undefined;
    private started = false;

    constructor(@Inject(DOCUMENT) private document: Document) { }

    public start(): void {
        if (this.started) return;

        this.started = true;
        const body = this.document.body as HTMLElement;

        this.applyFixesToTree(body);

        this.initObserver();
    }

    private applyFixesToTree(root: HTMLElement): void {
        this.applyWcagFixes(root);
        root.querySelectorAll('*').forEach((el) => {
            if (el instanceof HTMLElement) {
                this.applyWcagFixes(el);
            }
        });
    }

    public stop(): void {
        if (this.observer) {
            try {
                this.observer.disconnect();
            } finally {
                this.observer = undefined;
            }
        }
        this.started = false;
    }

    private initObserver(): void {
        this.observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node instanceof HTMLElement) {
                            this.applyWcagFixes(node);
                            node.querySelectorAll('*').forEach((child) => {
                                if (child instanceof HTMLElement) {
                                    this.applyWcagFixes(child);
                                }
                            });
                        }
                    });
                }
            }
        });

        this.observer.observe(this.document.body, {
            childList: true,
            subtree: true
        });
    }

    private applyWcagFixes(el: HTMLElement): void {
        this.fixDialogAccessibility(el);
        this.fixDialogCloseButton(el);
        this.fixPrimeNGCheckboxes(el);
        this.fixInvalidAriaRequired(el);
        this.fixScrollableContainers(el);
    }

    private setAttrIfMissing(el: Element, name: string, value: string): void {
        if (!el.hasAttribute(name)) el.setAttribute(name, value);
    }

    private ensureId(el: HTMLElement, prefix: string): string {
        if (el.id && el.id.trim() !== '') return el.id;
        const id = `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
        el.id = id;
        return id;
    }

    private fixDialogCloseButton(el: HTMLElement): void {
        if (el.tagName === 'BUTTON' && 
            (el.classList.contains('p-dialog-close-button') || 
             el.getAttribute('data-pc-name') === 'pcclosebutton' ||
             el.closest('.p-dialog-header'))) {
            
            const hasCloseIcon = el.querySelector('[data-pc-section="closeicon"]') || 
                                el.querySelector('.p-dialog-header-close-icon') ||
                                el.classList.contains('p-dialog-close-button');
            
            if (hasCloseIcon && (!el.hasAttribute('aria-label') || el.getAttribute('aria-label') === '')) {
                el.setAttribute('aria-label', 'Close dialog');
            }
        }
    }
    
    private fixDialogAccessibility(el: HTMLElement): void {
        if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') {
            this.setAttrIfMissing(el, 'aria-modal', 'true');
            const hasLabel = el.hasAttribute('aria-label');
            const hasLabelledBy = el.hasAttribute('aria-labelledby');
            if (!hasLabel && !hasLabelledBy) {
                const heading = el.querySelector('h1, h2, h3, .p-dialog-title') as HTMLElement | null;
                if (heading) {
                    const id = this.ensureId(heading, 'dialog-heading');
                    el.setAttribute('aria-labelledby', id);
                } else {
                    el.setAttribute('aria-label', 'Confirmation Dialog');
                }
            }
            this.setAttrIfMissing(el, 'title', 'Confirmation Dialog');
        }
    }

    private fixPrimeNGCheckboxes(el: HTMLElement): void {
        if (el.classList.contains('p-checkbox')) {
            const box = el.querySelector('.p-checkbox-box') as HTMLElement | null;
            const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
            const parentInteractive = el.closest('[role="option"], [role="row"], [role="button"], [role="link"], button, a');
            const isNestedInInteractive = parentInteractive && parentInteractive !== el;
            if (box) {
                if (!isNestedInInteractive) {
                    this.setAttrIfMissing(box, 'role', 'checkbox');
                    this.setAttrIfMissing(box, 'tabindex', '0');
                    if (!box.hasAttribute('aria-checked')) {
                        box.setAttribute('aria-checked', input?.checked ? 'true' : 'false');
                    }
                    if (!box.hasAttribute('aria-label') && !box.hasAttribute('aria-labelledby')) {
                        const label = input?.getAttribute('aria-label') ||
                            el.closest('.field, .form-group')?.querySelector('label')?.textContent?.trim() ||
                            'checkbox option';
                        box.setAttribute('aria-label', label);
                    }
                } else {
                    box.removeAttribute('role');
                    box.removeAttribute('tabindex');
                    box.removeAttribute('aria-checked');
                    box.removeAttribute('aria-label');
                    box.setAttribute('aria-hidden', 'true');
                    box.setAttribute('inert', '');
                    box.style.pointerEvents = 'none';
                }
            }

            if (input) {
                if (isNestedInInteractive) {
                    input.setAttribute('aria-hidden', 'true');
                    input.setAttribute('tabindex', '-1');
                    input.setAttribute('inert', '');
                    input.style.pointerEvents = 'none';
                } else {
                    input.removeAttribute('aria-hidden');
                    input.removeAttribute('inert');
                    input.style.pointerEvents = '';
                }
            }
        }
    }

    private fixInvalidAriaRequired(el: HTMLElement): void {
        if (!el.hasAttribute('aria-required') || el.getAttribute('role') === 'radiogroup') {
            return;
        }
        
        if (el.querySelector('input[type="radio"]')) {
            el.setAttribute('role', 'radiogroup');
        } else {
            el.removeAttribute('aria-required');
        }
    }

    private fixScrollableContainers(el: HTMLElement): void {
        if (el.classList.contains('p-select-list-container') || 
            el.classList.contains('p-autocomplete-list-container') ||
            el.classList.contains('p-autocomplete-items')) {
            const currentTabindex = el.getAttribute('tabindex');
            if (!currentTabindex || currentTabindex === '-1') {
                el.setAttribute('tabindex', '0');
            }
        }
        
        if (el.classList.contains('p-select-overlay') || 
            el.classList.contains('p-autocomplete-overlay')) {
            if (!el.hasAttribute('role')) {
                el.setAttribute('role', 'region');
            }
            if (!el.hasAttribute('aria-label')) {
                el.setAttribute('aria-label', 'Dropdown menu');
            }
        }
    }
}
